import { z } from 'zod';
import { ConsensusHistoryLogAction } from '../../forecast-types/src/lib/enums/consensus-history-log-action.enum';
import { Resolution } from '../../forecast-types/src/lib/enums/resolution.enum';

// helper for optional array query params (handles single value, array, or undefined) -> filters out empty strings/falsy values
const optionalUuidArray = z
  .union([z.string().uuid(), z.array(z.string().uuid())])
  .optional()
  .transform((val) => {
    if (val === undefined) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr.filter(Boolean);
  });

// helper for optional string array query params (non-uuid strings like column names)
const optionalStringArray = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((val) => {
    if (val === undefined) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr.filter(Boolean);
  });

/**
 * helper for json-encoded query params
 * safely parses json and returns zod error for invalid json
 */
const jsonParam = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .string()
    .transform((str, ctx) => {
      try {
        return JSON.parse(str) as unknown;
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid JSON',
        });
        return z.NEVER;
      }
    })
    .pipe(schema);

// sort model for ag grid server side sorting
export const sortModelItemSchema = z.object({
  colId: z.string(),
  sort: z.enum(['asc', 'desc']),
});

export const SortModelSchema = z.array(sortModelItemSchema);

export type SortModelItem = z.infer<typeof sortModelItemSchema>;
export type SortModel = z.infer<typeof SortModelSchema>;

// ag grid number filter: single condition
export const SimpleNumericFilterSchema = z.object({
  filterType: z.literal('number'),
  type: z.enum([
    'equals',
    'notEqual',
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ]),
  filter: z.number().optional(),
  filterTo: z.number().optional(),
});

// ag grid number filter: combined (AND/OR) — ag-grid 31+ uses conditions[]
export const CombinedNumericFilterSchema = z.object({
  filterType: z.literal('number'),
  operator: z.enum(['AND', 'OR']),
  conditions: z.array(SimpleNumericFilterSchema),
});

export const NumericColumnFilterSchema = z.union([
  CombinedNumericFilterSchema,
  SimpleNumericFilterSchema,
]);

// map of colId → numeric filter (only numeric columns send this)
export const FilterModelSchema = z.record(
  z.string(),
  NumericColumnFilterSchema,
);

export type SimpleNumericFilter = z.infer<typeof SimpleNumericFilterSchema>;
export type CombinedNumericFilter = z.infer<typeof CombinedNumericFilterSchema>;
export type NumericColumnFilter = z.infer<typeof NumericColumnFilterSchema>;
export type FilterModel = z.infer<typeof FilterModelSchema>;

/**
 * Body for POST /consensus/predictions/leaf/query
 *
 * `includeActuals` switches the queried pipeline series from the prediction to
 * the target (actual) values — the months before the forecast horizon are
 * planned against actuals, not a forecast.
 */
export const LeafPredictionsBodySchema = z.object({
  versionIds: z.array(z.string().uuid()),
  dateValues: z.array(z.string()).min(1),
  dimensionGroupIds: z.array(z.string().uuid()),
  includeActuals: z.boolean().optional(),
});

export type LeafPredictionsBody = z.infer<typeof LeafPredictionsBodySchema>;

const filterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

/**
 * Body for POST /consensus/predictions/aggregated/query
 *
 * See {@link LeafPredictionsBodySchema} for `includeActuals`.
 */
export const AggregatedPredictionsBodySchema = z.object({
  versionIds: z.array(z.string().uuid()),
  planningHierarchy: z.array(z.string()),
  dateValues: z.array(z.string()).min(1),
  filters: z.record(z.string(), z.array(filterValueSchema)),
  level: z.number().int().min(0),
  filterCombinationIds: z.array(z.string().uuid()).optional(),
  includeActuals: z.boolean().optional(),
});

export type AggregatedPredictionsBody = z.infer<
  typeof AggregatedPredictionsBodySchema
>;

/**
 * Body for POST /consensus/values/leaf/query
 *
 * Consensus counterpart of {@link LeafPredictionsBodySchema}: loads the
 * value-bearing consensus cells for a set of leaf dimension groups across the
 * requested months, decoupled from the row-structure query.
 */
export const LeafConsensusValuesBodySchema = z.object({
  configurationId: z.string().uuid(),
  consensusTemplateId: z.string().uuid(),
  dateValues: z.array(z.string()).min(1),
  dimensionGroupIds: z.array(z.string().uuid()),
});

export type LeafConsensusValuesBody = z.infer<
  typeof LeafConsensusValuesBodySchema
>;

/**
 * Body for POST /consensus/values/aggregated/query
 *
 * Computes the filter-aware SUM of leaf consensus values for each aggregated
 * row. `dimensions` carries the rows' own dimension combinations: the shared
 * parent prefix is applied as an index-friendly jsonb-containment scope (`@>`)
 * and the keys become the GROUP BY — the same path the old row-enrichment used.
 * Only the *applied* filters (`filterCombinationIds` + `rawFilters`) are added
 * as `->>` predicates, so the GIN index on `dimensions` stays usable.
 */
export const AggregatedConsensusValuesBodySchema = z.object({
  configurationId: z.string().uuid(),
  consensusTemplateId: z.string().uuid(),
  planningHierarchy: z.array(z.string()),
  dateValues: z.array(z.string()).min(1),
  dimensions: z.array(z.record(z.string(), filterValueSchema)),
  filterCombinationIds: z.array(z.string().uuid()).optional(),
  rawFilters: z.record(z.string(), z.array(filterValueSchema)).optional(),
});

export type AggregatedConsensusValuesBody = z.infer<
  typeof AggregatedConsensusValuesBodySchema
>;

// dateValues accepts either repeated query params (?dateValues=2026-01&dateValues=2026-02)
// or a single comma-separated value (?dateValues=2026-01,2026-02). yields a
// normalised non-empty string[]
const dateValuesParam = z
  .union([z.string(), z.array(z.string())])
  .transform((val, ctx) => {
    const arr = Array.isArray(val) ? val : val.split(',');
    const normalised = arr.map((s) => s.trim()).filter(Boolean);
    if (normalised.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'dateValues must contain at least one yyyy-MM entry',
      });
      return z.NEVER;
    }
    return normalised;
  });

/**
 * Query params for GET /consensus/:configurationId/dimension-groups
 */
export const DimensionGroupsQuerySchema = z.object({
  dateValues: dateValuesParam,
  filterCombinationIds: optionalUuidArray,
  planningHierarchy: optionalStringArray,
  sortingColumns: optionalStringArray,
  consensusTemplateId: z.string().uuid(),
  parentId: z.string().optional(),
  sortModel: jsonParam(SortModelSchema).optional(),
  filterModel: jsonParam(FilterModelSchema).optional(),
  filters: jsonParam(
    z.record(z.string(), z.array(filterValueSchema)),
  ).optional(),
  includeTotalRow: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});

export type DimensionGroupsQuery = z.infer<typeof DimensionGroupsQuerySchema>;

/**
 * Body for POST /consensus/cycles/:cycleId/columns/:columnId/cell-history
 *
 * Identifies a cell by its attribute combination (`dimensions` + `isLeaf`) and
 * month, and returns its history across every cycle of the configuration the
 * given cycle belongs to. `limit`/`offset` page the result newest-first; the
 * edit modal sends its own page sizes (two entries, then five per "Show more"),
 * so the default here is only what an unparameterised caller gets.
 */
export const CellHistoryBodySchema = z.object({
  dateValue: z.string(),
  dimensions: z.record(z.string(), filterValueSchema),
  isLeaf: z.boolean(),
  limit: z.number().int().positive().max(100).default(50),
  offset: z.number().int().min(0).default(0),
});

export type CellHistoryBody = z.infer<typeof CellHistoryBodySchema>;

/**
 * Actions a user request may write. RECONCILED and CYCLE_CARRIED are
 * system-generated (reconciliation cascade / carry-forward job) and must
 * never be accepted from a client.
 */
const userWritableConsensusHistoryLogActions = [
  ConsensusHistoryLogAction.CREATE,
  ConsensusHistoryLogAction.DELETE,
  ConsensusHistoryLogAction.LOCK,
  ConsensusHistoryLogAction.UNLOCK,
  ConsensusHistoryLogAction.RESET,
] as const;

/**
 * Body for POST /consensus/reconciliation/configurations/:id/templates/:id
 */
export const CreateConsensusReconciledHistoryLogsSchema = z.object({
  action: z.enum(userWritableConsensusHistoryLogActions),
  dimensions: z.record(z.string(), filterValueSchema),
  consensusValue: z.number().nullable(),
  comment: z.string().max(100).nullable(),
  consensusColumnId: z.string().uuid(),
  resolution: z.nativeEnum(Resolution),
  dateValue: z.string(),
  isLeaf: z.boolean(),
  filterCombinationIds: optionalUuidArray,
  rawFilters: z
    .record(z.string(), z.array(filterValueSchema))
    .optional()
    .transform((val) => val ?? undefined),
  /** active planning hierarchy at edit time — persisted as provenance */
  planningHierarchy: z.array(z.string()).optional(),
  /**
   * accepted and ignored: the server stamps the job-resolved forecast
   * version. Forward-compat for pinning the cycle version (PM-4071).
   */
  forecastVersionId: z.string().uuid().optional(),
  /** client-generated per request; a retry with the same id reuses the job */
  clientRequestId: z.string().uuid().optional(),
  /**
   * Newest direct edit (CREATE / RESET / DELETE) of this cell the client has
   * seen, null when it saw none. Omitted = no conflict check. A different
   * newest direct edit on the server is answered with 409 unless `force`.
   */
  lastSeenLogId: z.string().uuid().nullable().optional(),
  /** server time of the overwrite preview this edit was confirmed against */
  previewedAt: z.string().datetime().optional(),
  /** overwrite a conflicting edit the planner has explicitly confirmed */
  force: z.boolean().optional(),
  /**
   * Months of one multi-month edit share a batch; the end event of the
   * whole batch is sent once the last month's job has finished.
   */
  batchId: z.string().uuid().optional(),
  batchSize: z.number().int().positive().optional(),
});

export type CreateConsensusReconciledHistoryLogsBody = z.infer<
  typeof CreateConsensusReconciledHistoryLogsSchema
>;

/**
 * Body for POST /consensus/reconciliation/node-versions/cycles/:id/templates/:id
 */
export const ConsensusNodeVersionsSchema = z.object({
  dimensions: z.record(z.string(), filterValueSchema),
  isLeaf: z.boolean(),
  consensusColumnId: z.string().uuid(),
  dateValues: z.array(z.string()).min(1).max(60),
});

export type ConsensusNodeVersionsBody = z.infer<
  typeof ConsensusNodeVersionsSchema
>;

export const ConsensusOverwritePreviewSchema = z.object({
  dimensions: z.record(z.string(), filterValueSchema),
  consensusColumnId: z.string().uuid(),
  dateValue: z.string(),
  filterCombinationIds: optionalUuidArray,
  rawFilters: z
    .record(z.string(), z.array(filterValueSchema))
    .optional()
    .transform((val) => val ?? undefined),
});

export type ConsensusOverwritePreviewBody = z.infer<
  typeof ConsensusOverwritePreviewSchema
>;

/**
 * Body for POST /consensus/templates/:id/columns and PUT /consensus/templates/columns/:id
 */
export const ConsensusColumnNameSchema = z.object({
  name: z.string().trim().min(2).max(32),
});

export type ConsensusColumnNameBody = z.infer<typeof ConsensusColumnNameSchema>;
